import React, { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { StaffSessionContext } from '@/types/index';
import { getRiderSession, logoutRider, type RiderSession } from '@/services/riderAuthService';
import { useNavigate } from 'react-router-dom';
import { useCacheStore } from '@/stores/cacheStore';

// Storage keys to clear on logout (must match BranchContext and other contexts)
const USER_STORAGE_KEYS = [
  'menu-maven-selected-branch',
  'menu-maven-prefetch-status'
] as const;

// Helper to log auth events
const logAuthEvent = async (
  restaurantId: string,
  userId: string,
  userName: string,
  action: 'login' | 'logout'
) => {
  try {
    await supabase.rpc('log_auth_event', {
      p_restaurant_id: restaurantId,
      p_user_id: userId,
      p_user_name: userName,
      p_action: action,
      p_ip_address: null,
      p_user_agent: navigator.userAgent
    });
  } catch (error) {
    console.error('Failed to log auth event:', error);
  }
};

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  authenticating: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  isOwner: boolean;
  // Staff session context
  staffSession: StaffSessionContext | null;
  isStaff: boolean;
  staffRole: string | null;
  clearStaffSession: () => void;
  setStaffSession: (session: StaffSessionContext) => void;
  // Rider session context
  riderSession: RiderSession | null;
  isRider: boolean;
  clearRiderSession: () => void;
  setRiderSession: (session: RiderSession) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [authenticating, setAuthenticating] = useState(false);
  const [staffSession, setStaffSession] = useState<StaffSessionContext | null>(null);
  const [riderSession, setRiderSession] = useState<RiderSession | null>(null);
  const [riderLoading, setRiderLoading] = useState(true);
  const navigate = useNavigate();

  // Load staff session from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('staff_session');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Validate the session has required fields
        if (parsed && parsed.id && parsed.restaurant_id) {
          setStaffSession(parsed);
        } else {
          console.warn('Invalid staff session data, clearing...');
          localStorage.removeItem('staff_session');
        }
      } catch (error) {
        console.error('Failed to parse staff session:', error);
        localStorage.removeItem('staff_session');
      }
    }
  }, []);

  // Load rider session from localStorage
  useEffect(() => {
    const session = getRiderSession();
    if (session) {
      setRiderSession(session);
    }
    setRiderLoading(false);
  }, []);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    setAuthenticating(true);
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error: error as Error | null };
      }

      // Check if user profile is active
      if (data.user) {
        try {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('status')
            .eq('id', data.user.id)
            .single();

          if (profileError && profileError.code !== 'PGRST116') {
            console.error('Error fetching profile:', profileError);
            return { error: new Error('Failed to verify account status') };
          }

          // If profile exists and status is inactive, return error
          if (profile && profile.status === 'inactive') {
            // Sign out the user since they're inactive
            await supabase.auth.signOut();
            return { error: new Error('Your subscription has ended') };
          }
        } catch (profileError) {
          console.error('Error checking profile status:', profileError);
          return { error: new Error('Failed to verify account status') };
        }

        // Log the login event
        try {
          const { data: restaurant } = await supabase
            .from('restaurants')
            .select('id')
            .eq('owner_id', data.user.id)
            .single();
          
          if (restaurant) {
            await logAuthEvent(
              restaurant.id,
              data.user.id,
              data.user.email || 'Unknown User',
              'login'
            );
          }
        } catch (logError) {
          console.error('Failed to log login event:', logError);
        }
      }

      return { error: null };
    } finally {
      setAuthenticating(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const clearStaffSession = useCallback(async () => {
    // Log staff logout event before clearing session
    if (staffSession) {
      try {
        await supabase.from('audit_logs').insert({
          restaurant_id: staffSession.restaurant_id,
          staff_id: staffSession.id,
          user_type: 'staff',
          user_name: staffSession.username,
          action: 'logout',
          entity_type: 'authentication',
          entity_id: staffSession.id,
          entity_name: staffSession.username,
          changes: {},
          metadata: { event: 'logout' }
        });
      } catch (logError) {
        console.error('Failed to log staff logout event:', logError);
      }
    }
    setStaffSession(null);
    localStorage.removeItem('staff_session');
  }, [staffSession]);

  const clearRiderSession = useCallback(async () => {
    // Log rider logout event before clearing session
    if (riderSession) {
      try {
        await supabase.from('audit_logs').insert({
          restaurant_id: riderSession.restaurant_id,
          staff_id: riderSession.id,
          user_type: 'rider',
          user_name: riderSession.name,
          action: 'logout',
          entity_type: 'authentication',
          entity_id: riderSession.id,
          entity_name: riderSession.name,
          changes: {},
          metadata: { event: 'logout' }
        });
      } catch (logError) {
        console.error('Failed to log rider logout event:', logError);
      }
    }
    logoutRider();
    setRiderSession(null);
  }, [riderSession]);

  const signOut = useCallback(async () => {
    try {
      // Log the logout event before clearing session
      if (user) {
        try {
          const { data: restaurant } = await supabase
            .from('restaurants')
            .select('id')
            .eq('owner_id', user.id)
            .single();
          
          if (restaurant) {
            await logAuthEvent(
              restaurant.id,
              user.id,
              user.email || 'Unknown User',
              'logout'
            );
          }
        } catch (logError) {
          console.error('Failed to log logout event:', logError);
        }
      }

      // Clear staff session first
      await clearStaffSession();

      // Clear rider session
      await clearRiderSession();

      // Clear IndexedDB cache to prevent data leakage between users
      try {
        await useCacheStore.getState().clearUserContext();
        console.log('[AuthContext] Cleared IndexedDB cache on logout');
      } catch (cacheError) {
        console.error('[AuthContext] Failed to clear cache on logout:', cacheError);
      }

      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Supabase sign out error:', error);
      }
    } finally {
      // Ensure all local state and storage is cleared regardless of errors
      setUser(null);
      setSession(null);
      setStaffSession(null);
      setRiderSession(null);
      localStorage.removeItem('staff_session');

      // Clear user-specific storage keys (branch selection, prefetch status, etc.)
      USER_STORAGE_KEYS.forEach(key => {
        localStorage.removeItem(key);
        console.log(`[AuthContext] Cleared localStorage key: ${key}`);
      });

      // Explicitly clear Supabase auth tokens from localStorage
      // Supabase uses keys like sb-<project-ref>-auth-token
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
          localStorage.removeItem(key);
        }
      });

      // Use navigate instead of window.location.replace to avoid race conditions
      navigate('/');
    }
  }, [navigate, user, clearStaffSession, clearRiderSession]);

  const value = useMemo(() => ({
    user,
    session,
    loading: loading || riderLoading,
    authenticating,
    signIn,
    signUp,
    signOut,
    isOwner: !!user,
    staffSession,
    isStaff: !!staffSession,
    staffRole: staffSession?.access_permissions ? 'CUSTOM' : null,
    clearStaffSession,
    setStaffSession,
    riderSession,
    isRider: !!riderSession,
    clearRiderSession,
    setRiderSession,
  }), [user, session, loading, riderLoading, authenticating, staffSession, riderSession, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
